import React, { useState, useEffect } from 'react';
import { User, Plus, ShoppingBag, CalendarCheck, PhoneCall, Ban, CalendarSync, Settings, HeartPulse } from 'lucide-react';
import { TIME_SLOTS_CONFIG } from '../../../constants/calendar';
import { calculateAppointmentPosition, getAppointmentTypeClass, AppointmentServceUtils } from '../../../utils/appointmentUtils';
import { AppointmentService } from '../../../services/appointmentService';
import { AppointmentServiceUtils } from '../../../utils/appointmentUtils';
import { appointmentSyncService } from '../../../services/appointmentSyncService';
import { formatTime } from '../../../utils/dateUtils';
import { ERROR_MESSAGES } from '../../../constants/calendar';
import '../styles/DayView.css';

const DayView = ({
  currentDate,
  appointments,
  setAppointments,
  timeSlots,
  currentOfficeStaff,
  selectedStylist,
  dayHolidays,
  highlightedAppointment,
  onTimeSlotClick,
  onAppointmentClick,
  onError,
  useMilitaryTime = false,
  lunchBreakSettings = {},
  onLunchBreakClick = null,
  onLunchBreakSettingsChange = null
}) => {
  // Which stylist's lunch settings popover is open: { stylistId, x, y } or null
  const [lunchSettingsFor, setLunchSettingsFor] = useState(null);
  const lunchPopoverRef = React.useRef(null);

  // Lunch band drag state
  const [draggingLunch, setDraggingLunch] = useState(null);
  // { stylistId, startY, origStartHour, origStartMinute, previewHour, previewMinute, hasMoved }
  // Determine if we're in mobile mode and should show single stylist
  const isMobile = window.innerWidth <= 768;
  const showSingleStylist = isMobile && selectedStylist;
  const [currentTime, setCurrentTime] = React.useState(new Date());

    // Drag and resize state
    const [draggedAppointment, setDraggedAppointment] = useState(null);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState(null);
    const [resizeStartY, setResizeStartY] = useState(0);
    const [resizeAppointmentId, setResizeAppointmentId] = useState(null);
    const [originalAppointment, setOriginalAppointment] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartY, setDragStartY] = useState(0);
    const [dragAppointmentId, setDragAppointmentId] = useState(null);
    const [resizePreview, setResizePreview] = useState(null);
    const [dragPreview, setDragPreview] = useState(null);
  
  // Filter stylists based on mobile/desktop mode
  const stylistsToShow = showSingleStylist 
    ? [selectedStylist] 
    : currentOfficeStaff;

  React.useEffect(() => {
    const updateCurrentTime = () => setCurrentTime(new Date());
    
    // Update immediately
    updateCurrentTime();
    
    // Update every minute
    const interval = setInterval(updateCurrentTime, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Helper function to render status icons for appointments
const renderStatusIcons = (appointment) => {
  const icons = [];
  
  // Ready to pickup icon
  if (appointment.isReadyToPickup) {
    icons.push(
      <ShoppingBag
        key="pickup"
        size={18}
        style={{ color: '#000000ff', marginLeft: '4px' }}
        title="Ready to Pickup"
      />
    );
  }

  // DiNi Care client icon
  if (appointment.bDiniCare === 1 || appointment.bDiniCare === true) {
    icons.push(
      <HeartPulse
        key="dinicare"
        size={18}
        style={{ color: '#000000ff', marginLeft: '4px' }}
        title="DiNi Care Client"
      />
    );
  }

  // Status-based icons
  switch (appointment.AppointmentStatus) {
    case 'Confirmed':
      icons.push(
        <CalendarCheck
          key="confirmed"
          size={18}
          style={{ color: '#000000ff', marginLeft: '4px' }}
          title="Confirmed"
        />
      );
      break;
    case 'Called':
    case 'Called - No answer':
      icons.push(
        <PhoneCall
          key="called"
          size={18}
          style={{ color: '#000000ff', marginLeft: '4px' }}
          title={appointment.AppointmentStatus}
        />
      );
      break;
    case 'Cancelled':
    case 'No Show':
      icons.push(
        <Ban
          key="cancelled"
          size={18}
          style={{ color: '#dc2626', marginLeft: '4px' }}
          title={appointment.AppointmentStatus}
        />
      );
      break;
    case 'Reschedule Requested':
      icons.push(
        <CalendarSync
          key="reschedule"
          size={18}
          style={{ color: '#000000ff', marginLeft: '4px' }}
          title="Reschedule Requested"
        />
      );
      break;
    default:
      break;
  }
  
  return icons;
};
// Add this helper function after renderStatusIcons
const calculateOverlapLayout = (appointments, timeSlots) => {
  const layoutInfo = {};
  
  // Group appointments into overlap groups
  const overlapGroups = [];
  
  appointments.forEach(apt => {
    const aptStart = new Date(apt.EventStart);
    const aptEnd = new Date(apt.EventEnd);
    
    // Find if this appointment belongs to any existing group
    let foundGroup = null;
    
    for (let group of overlapGroups) {
      const overlapsWithGroup = group.some(groupApt => {
        const groupStart = new Date(groupApt.EventStart);
        const groupEnd = new Date(groupApt.EventEnd);
        
        // Check if appointments overlap (including same start times)
        return aptStart < groupEnd && aptEnd > groupStart;
      });
      
      if (overlapsWithGroup) {
        foundGroup = group;
        break;
      }
    }
    
    if (foundGroup) {
      foundGroup.push(apt);
    } else {
      overlapGroups.push([apt]);
    }
  });
  
  // Now assign layout info for each group
  overlapGroups.forEach(group => {
    if (group.length === 1) {
      // No overlap - full width
      layoutInfo[group[0].ID] = {
        hasOverlap: false,
        positionInGroup: 0,
        totalInGroup: 1,
        width: '100%',
        left: '0%'
      };
    } else {
      // Sort group by start time, then by ID for consistent ordering
      group.sort((a, b) => {
        const startDiff = new Date(a.EventStart) - new Date(b.EventStart);
        if (startDiff === 0) {
          return a.ID - b.ID; // Use ID as tiebreaker for same start times
        }
        return startDiff;
      });
      
      // Assign positions
      group.forEach((apt, index) => {
        layoutInfo[apt.ID] = {
          hasOverlap: true,
          positionInGroup: index,
          totalInGroup: group.length,
          width: `${100 / group.length}%`,
          left: `${(index * 100) / group.length}%`
        };
      });
    }
  });
  
  return layoutInfo;
};


  const getCurrentTimePosition = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // Calculate minutes from start hour (same logic as appointment positioning)
    const minutesFromBase = (currentHour - TIME_SLOTS_CONFIG.START_HOUR) * 60 + currentMinute;
    
    // Calculate pixel position
    const pixelPosition = minutesFromBase * TIME_SLOTS_CONFIG.PIXELS_PER_MINUTE;
    
    return {
      top: pixelPosition,
      isVisible: currentHour >= TIME_SLOTS_CONFIG.START_HOUR && 
                currentHour <= (TIME_SLOTS_CONFIG.END_HOUR || 20)
    };
  };


  
  const handleVerticalDragStart = (e, appointment) => {
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragAppointmentId(appointment.ID);
    
    // Add initial drag preview with current times (already snapped)
    const startTime = new Date(appointment.EventStart);
    const endTime = new Date(appointment.EventEnd);
    const duration = Math.round((endTime - startTime) / (1000 * 60));

    setDragPreview({
      startTime: formatTime(startTime, useMilitaryTime),
      endTime: formatTime(endTime, useMilitaryTime),
      duration: duration,
      mouseX: e.clientX,
      mouseY: e.clientY,
      type: 'drag'
    });
    
    e.dataTransfer.effectAllowed = 'move';
  };
const handleDragMove = (e) => {
  if (!isDragging || !dragAppointmentId) return;
  
  const deltaY = e.clientY - dragStartY;
  const minutesChange = Math.round(deltaY / (TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX / 15));
  
  const draggedApt = appointments.find(apt => apt.ID === dragAppointmentId);
  if (!draggedApt) return;
  
  // Calculate new times with 15-minute snapping
  const originalStart = new Date(draggedApt.EventStart);
  const originalEnd = new Date(draggedApt.EventEnd);
  const duration = originalEnd - originalStart;
  
  // Apply 15-minute interval changes only
  const snappedMinutesChange = Math.round(minutesChange / 15) * 15;
  
  const newStart = new Date(originalStart.getTime() + (snappedMinutesChange * 60 * 1000));
  const newEnd = new Date(newStart.getTime() + duration);
  
  // Calculate the preview position on the grid
  const previewStartHour = newStart.getHours();
  const previewStartMinute = newStart.getMinutes();
  const previewStartMinutesFromBase = (previewStartHour - TIME_SLOTS_CONFIG.START_HOUR) * 60 + previewStartMinute;
  const previewStartSlotIndex = Math.floor(previewStartMinutesFromBase / 15);
  const previewDurationMinutes = (newEnd - newStart) / (1000 * 60);
  const previewHeight = previewDurationMinutes * (TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX / 15);
  
  // Update drag preview with both tooltip and grid position info
  setDragPreview({
    startTime: formatTime(newStart, useMilitaryTime),
    endTime: formatTime(newEnd, useMilitaryTime),
    duration: Math.round(duration / (1000 * 60)),
    mouseX: e.clientX,
    mouseY: e.clientY,
    type: 'drag',
    // Grid position info for visual preview
    gridTop: previewStartSlotIndex * TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX,
    gridHeight: previewHeight,
    isValid: previewStartHour >= TIME_SLOTS_CONFIG.START_HOUR && previewStartHour <= (TIME_SLOTS_CONFIG.END_HOUR || 20)
  });
};

// Helper: format a time (hour, minute) as 12h or 24h string
const formatLunchTime = (hour, minute) => {
  if (useMilitaryTime) {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${h12}:${minute.toString().padStart(2, '0')} ${ampm}`;
};

// Helper: render the lunch break band for a given stylist
const renderLunchBreak = (stylist) => {
  const s = lunchBreakSettings[stylist.id];
  if (!s?.enabled) return null;

  const isDraggingThis = draggingLunch?.stylistId === stylist.id;
  const startHour = isDraggingThis && draggingLunch.hasMoved
    ? (draggingLunch.previewHour ?? s.startHour)
    : s.startHour;
  const startMinute = isDraggingThis && draggingLunch.hasMoved
    ? (draggingLunch.previewMinute ?? s.startMinute)
    : s.startMinute;
  const { duration } = s;

  const minutesFromBase = (startHour - TIME_SLOTS_CONFIG.START_HOUR) * 60 + startMinute;
  if (minutesFromBase < 0) return null;

  const topPosition = minutesFromBase * TIME_SLOTS_CONFIG.PIXELS_PER_MINUTE;
  const height = duration * TIME_SLOTS_CONFIG.PIXELS_PER_MINUTE;

  const endTotalMinutes = startHour * 60 + startMinute + duration;
  const endHour = Math.floor(endTotalMinutes / 60);
  const endMinute = endTotalMinutes % 60;

  const startLabel = formatLunchTime(startHour, startMinute);
  const endLabel = formatLunchTime(endHour, endMinute);

  return (
    <div
      key={`lunch-${stylist.id}`}
      className={`lunch-break-band${isDraggingThis ? ' lunch-dragging' : ''}`}
      style={{ top: `${topPosition}px`, height: `${height}px`, cursor: isDraggingThis ? 'grabbing' : 'grab' }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggingLunch({
          stylistId: stylist.id,
          startY: e.clientY,
          origStartHour: s.startHour,
          origStartMinute: s.startMinute,
          previewHour: s.startHour,
          previewMinute: s.startMinute,
          hasMoved: false
        });
      }}
      title="Drag to change time · click to schedule"
    >
      <span className="lunch-break-icon">🍽</span>
      <span className="lunch-break-label">Lunch {startLabel} – {endLabel}</span>
    </div>
  );
};

// Helper function to generate hour divider lines
const generateHourLines = () => {
  const lines = [];
  for (let hour = TIME_SLOTS_CONFIG.START_HOUR; hour <= (TIME_SLOTS_CONFIG.END_HOUR || 20); hour++) {
    // Calculate position (each hour = 4 slots of 15 minutes each)
    const hourSlotIndex = (hour - TIME_SLOTS_CONFIG.START_HOUR) * 4;
    const topPosition = hourSlotIndex * TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX;

    // Determine if this is a major hour (12pm, 6pm, etc.)
    const isMajorHour = hour === 12 || hour === 18 || hour === 6;

    // Format time label based on military time preference
    let timeLabel;
    if (useMilitaryTime) {
      timeLabel = `${hour.toString().padStart(2, '0')}:00`;
    } else {
      timeLabel = hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
    }

    lines.push({
      hour,
      topPosition,
      isMajorHour,
      timeLabel
    });
  }
  return lines;
};
  
  // Resize handlers
  const handleResizeStart = (e, appointment, direction) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeDirection(direction);
    setResizeStartY(e.clientY);
    setResizeAppointmentId(appointment.ID);
    setOriginalAppointment(appointment);
    
    console.log(`Starting resize ${direction} for appointment:`, appointment.ClientName);
  };
const handleResizeMove = (e) => {
  if (!isResizing || !resizeAppointmentId || !originalAppointment) return;
  
  e.preventDefault();
  const deltaY = e.clientY - resizeStartY;
  
  // Calculate how many 15-minute slots the mouse has moved
  const slotsChanged = Math.round(deltaY / TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX);
  const minutesChange = slotsChanged * 15; // Each slot is 15 minutes
  
  // Calculate preview dimensions
  const originalStart = new Date(originalAppointment.EventStart);
  const originalEnd = new Date(originalAppointment.EventEnd);
  
  let previewStart, previewEnd;
  
  if (resizeDirection === 'top') {
    // Resizing from top - change start time
    previewStart = new Date(originalStart.getTime() + (minutesChange * 60 * 1000));
    previewEnd = originalEnd;
    
    // Ensure minimum 15-minute duration
    if (previewEnd - previewStart < 15 * 60 * 1000) {
      previewStart = new Date(previewEnd.getTime() - (15 * 60 * 1000));
    }
  } else {
    // Resizing from bottom - change end time
    previewStart = originalStart;
    previewEnd = new Date(originalEnd.getTime() + (minutesChange * 60 * 1000));
    
    // Ensure minimum 15-minute duration
    if (previewEnd - previewStart < 15 * 60 * 1000) {
      previewEnd = new Date(previewStart.getTime() + (15 * 60 * 1000));
    }
  }
  
  // Calculate preview position and size
  const previewDuration = (previewEnd - previewStart) / (1000 * 60);
  const previewStartHour = previewStart.getHours();
  const previewStartMinute = previewStart.getMinutes();
  const previewStartMinutesFromBase = (previewStartHour - TIME_SLOTS_CONFIG.START_HOUR) * 60 + previewStartMinute;
  const previewStartSlotIndex = Math.floor(previewStartMinutesFromBase / 15);
  const previewHeight = previewDuration * (TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX / 15);
  
  setResizePreview({
    top: previewStartSlotIndex * TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX + 4,
    height: previewHeight,
    duration: previewDuration,
    startTime: formatTime(previewStart, useMilitaryTime),
    endTime: formatTime(previewEnd, useMilitaryTime),
    mouseX: e.clientX,
    mouseY: e.clientY
  });

  // Also update drag preview for tooltip
  setDragPreview({
    startTime: formatTime(previewStart, useMilitaryTime),
    endTime: formatTime(previewEnd, useMilitaryTime),
    duration: Math.round(previewDuration),
    mouseX: e.clientX,
    mouseY: e.clientY,
    type: 'resize',
    direction: resizeDirection
  });
};
  
const handleResizeEnd = async (e) => {
  if (!isResizing || !resizeAppointmentId || !originalAppointment) return;
  
  const deltaY = e.clientY - resizeStartY;
  
  // Calculate how many 15-minute slots the mouse has moved
  const slotsChanged = Math.round(deltaY / TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX);
  const minutesChange = slotsChanged * 15; // Each slot is 15 minutes
  
  // Don't update if no slots changed
  if (slotsChanged === 0) {
    setIsResizing(false);
    setResizeDirection(null);
    setResizeAppointmentId(null);
    setOriginalAppointment(null);
    setResizePreview(null);
    setDragPreview(null);
    return;
  }
  
  try {
    const originalStart = new Date(originalAppointment.EventStart);
    const originalEnd = new Date(originalAppointment.EventEnd);
    
    let newStart, newEnd;
    
    if (resizeDirection === 'top') {
      // Resizing from top - change start time
      newStart = new Date(originalStart.getTime() + (minutesChange * 60 * 1000));
      newEnd = originalEnd;
      
      // Ensure minimum 15-minute duration
      if (newEnd - newStart < 15 * 60 * 1000) {
        newStart = new Date(newEnd.getTime() - (15 * 60 * 1000));
      }
    } else {
      // Resizing from bottom - change end time
      newStart = originalStart;
      newEnd = new Date(originalEnd.getTime() + (minutesChange * 60 * 1000));
      
      // Ensure minimum 15-minute duration
      if (newEnd - newStart < 15 * 60 * 1000) {
        newEnd = new Date(newStart.getTime() + (15 * 60 * 1000));
      }
    }
    
    const newStartDateTime = AppointmentServiceUtils.formatLocalDateTime(newStart);
    const newEndDateTime = AppointmentServiceUtils.formatLocalDateTime(newEnd);

    console.log('🔄 Resizing appointment with sync:', { resizeAppointmentId, newStartDateTime, newEndDateTime });

    // 🔗 Use sync service to update appointment AND linked order
    const syncResult = await appointmentSyncService.syncAppointmentToOrder(
      resizeAppointmentId,
      newStartDateTime,
      newEndDateTime
    );

    if (syncResult.success) {
      // Update appointments in state
      setAppointments(prev =>
        prev.map(apt =>
          apt.ID === resizeAppointmentId
            ? {
                ...apt,
                EventStart: newStartDateTime + ':00',
                EventEnd: newEndDateTime + ':00',
                EnteredBy: syncResult.enteredBy || apt.EnteredBy
              }
            : apt
        )
      );

      onError(''); // Clear any errors
    } else {
      throw new Error(syncResult.error || 'Failed to sync appointment');
    }
    
  } catch (error) {
    console.error('Error resizing appointment:', error);
    onError(`${ERROR_MESSAGES.RESIZE_APPOINTMENT}: ${error.message}`);
  } finally {
    setIsResizing(false);
    setResizeDirection(null);
    setResizeAppointmentId(null);
    setOriginalAppointment(null);
    setResizePreview(null);
    setDragPreview(null);
  }
};
  // Add mouse move listeners for drag (add this useEffect)
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = (e) => {
        setIsDragging(false);
        setDragAppointmentId(null);
        setDragPreview(null);
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStartY, dragAppointmentId]);
  
  
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e) => handleResizeMove(e);
      const handleMouseUp = (e) => handleResizeEnd(e);

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, resizeStartY, resizeAppointmentId, originalAppointment, resizeDirection]);

  // Close lunch settings popover on outside click
  useEffect(() => {
    if (!lunchSettingsFor) return;
    const handler = (e) => {
      if (lunchPopoverRef.current && !lunchPopoverRef.current.contains(e.target)) {
        setLunchSettingsFor(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [lunchSettingsFor]);

  // Lunch band drag: move on mousemove, commit or click on mouseup
  useEffect(() => {
    if (!draggingLunch) return;

    const onMouseMove = (e) => {
      const deltaY = e.clientY - draggingLunch.startY;
      if (Math.abs(deltaY) < 3) return; // ignore tiny jitter

      const rawDelta = deltaY / TIME_SLOTS_CONFIG.PIXELS_PER_MINUTE;
      const snappedDelta = Math.round(rawDelta / 15) * 15;

      const origTotal = draggingLunch.origStartHour * 60 + draggingLunch.origStartMinute;
      const s = lunchBreakSettings[draggingLunch.stylistId];
      const duration = s?.duration || 30;

      const minM = TIME_SLOTS_CONFIG.START_HOUR * 60;
      const maxM = TIME_SLOTS_CONFIG.END_HOUR * 60 - duration;
      const newTotal = Math.max(minM, Math.min(maxM, origTotal + snappedDelta));

      setDraggingLunch(prev => ({
        ...prev,
        previewHour: Math.floor(newTotal / 60),
        previewMinute: newTotal % 60,
        hasMoved: Math.abs(snappedDelta) >= 15
      }));
    };

    const onMouseUp = () => {
      if (draggingLunch.hasMoved) {
        onLunchBreakSettingsChange && onLunchBreakSettingsChange(draggingLunch.stylistId, {
          startHour: draggingLunch.previewHour ?? draggingLunch.origStartHour,
          startMinute: draggingLunch.previewMinute ?? draggingLunch.origStartMinute
        });
      } else {
        // Treat as a click — open appointment modal
        const stylist = stylistsToShow.find(s => s.id === draggingLunch.stylistId);
        if (stylist) onLunchBreakClick && onLunchBreakClick(currentDate, stylist);
      }
      setDraggingLunch(null);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [draggingLunch]);
  
  const snapToFifteenMinutes = (date) => {
  const minutes = date.getMinutes();
  const snappedMinutes = Math.round(minutes / 15) * 15;
  
  const snappedDate = new Date(date);
  snappedDate.setMinutes(snappedMinutes, 0, 0);
  
  // Handle hour overflow
  if (snappedMinutes === 60) {
    snappedDate.setHours(snappedDate.getHours() + 1);
    snappedDate.setMinutes(0);
  }
  
  return snappedDate;
};

const handleDragStart = (e, appointment) => {
  setDraggedAppointment(appointment);
  e.dataTransfer.effectAllowed = 'move';
  e.target.classList.add('dragging');
  
  // Initialize vertical drag tracking for snapped preview
  handleVerticalDragStart(e, appointment);
};

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedAppointment(null);
    setIsDragging(false);
    setDragAppointmentId(null);
    setDragPreview(null); // Clear the preview
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

const handleDrop = async (e, date, timeSlot = null, targetStylist = null) => {
  e.preventDefault();
  e.currentTarget.classList.remove('drag-over');
  
  if (!draggedAppointment) return;

  try {
    // Calculate new start and end times - KEEP IN LOCAL TIMEZONE
    let newStartTime, newEndTime;
    
    if (timeSlot) {
      const { hours, minutes } = AppointmentServiceUtils.parseTimeSlot(timeSlot);
      
      // Create date in local timezone (not UTC)
      const rawStartTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);
      
      // Snap to 15-minute intervals
      newStartTime = snapToFifteenMinutes(rawStartTime);
      
      // Calculate duration of original appointment
      const originalStart = new Date(draggedAppointment.EventStart);
      const originalEnd = new Date(draggedAppointment.EventEnd);
      const durationMs = originalEnd - originalStart;
      
      newEndTime = new Date(newStartTime.getTime() + durationMs);
    } else {
      // Default to 9 AM if no time slot specified
      const rawStartTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0, 0, 0);
      newStartTime = snapToFifteenMinutes(rawStartTime);
      const rawEndTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 10, 0, 0, 0);
      newEndTime = snapToFifteenMinutes(rawEndTime);
    }

    // Prepare the request body with LOCAL datetime strings
    const requestBody = {
      EventStart: AppointmentServiceUtils.formatLocalDateTime(newStartTime),
      EventEnd: AppointmentServiceUtils.formatLocalDateTime(newEndTime)
    };

    console.log('Original drop times (local):', {
      newStartTime: newStartTime.toLocaleString(),
      newEndTime: newEndTime.toLocaleString(),
      timeSlot: timeSlot
    });
    
    console.log('Formatted for API:', requestBody);

    console.log('🔄 Moving appointment with sync:', draggedAppointment.ID, 'with data:', requestBody);

    // 🔗 Use sync service to update appointment AND linked order
    const syncResult = await appointmentSyncService.syncAppointmentToOrder(
      draggedAppointment.ID,
      requestBody.EventStart,
      requestBody.EventEnd
    );

    if (syncResult.success) {
      // Update the appointment in local state
      setAppointments(prev =>
        prev.map(apt =>
          apt.ID === draggedAppointment.ID
            ? {
                ...apt,
                EventStart: requestBody.EventStart + ':00', // Add seconds for consistency
                EventEnd: requestBody.EventEnd + ':00',      // Add seconds for consistency
                EnteredBy: syncResult.enteredBy || apt.EnteredBy,
                // Handle stylist change separately if needed
                ...(targetStylist && targetStylist.id !== draggedAppointment.MWuserID && {
                  MeetingWith: targetStylist.name,
                  MWuserID: targetStylist.id
                })
              }
            : apt
        )
      );

      // If stylist changed, update that separately
      if (targetStylist && targetStylist.id !== draggedAppointment.MWuserID) {
        console.log('Moving appointment to different stylist:', targetStylist.name);
        await AppointmentService.updateAppointment(draggedAppointment.ID, {
          EventDesc: draggedAppointment.EventDesc,
          ClientName: draggedAppointment.ClientName,
          Title: draggedAppointment.Title,
          EventStart: requestBody.EventStart,
          EventEnd: requestBody.EventEnd,
          MeetingWith: targetStylist.name,
          MWuserID: targetStylist.id,
          ClientID: draggedAppointment.ClientID,
          PhoneNum: draggedAppointment.PhoneNum,
          checkedinby: draggedAppointment.checkedinby,
          orderno: draggedAppointment.orderno,
          AppointmentStatus: draggedAppointment.AppointmentStatus,
          AppointmentNotes: draggedAppointment.AppointmentNotes,
          isDiniReq: draggedAppointment.isDiniReq,
          isReadyToPickup: draggedAppointment.isReadyToPickup
        });
      }

      onError('');
    } else {
      throw new Error(syncResult.error || 'Failed to sync appointment');
    }

  } catch (error) {
    console.error('Error moving appointment:', error);
    onError(`${ERROR_MESSAGES.MOVE_APPOINTMENT}: ${error.message}`);
  } finally {
    setDraggedAppointment(null);
  }
};

  return (
    <div className="day-view-all-stylists">
      {/* Holiday Banner */}
      {dayHolidays.length > 0 && (
        <div className="holiday-banner">
          {dayHolidays.map(holiday => (
            <div key={holiday.ID} className="holiday-item">
              <span className="holiday-icon">🕎</span>
              <span className="holiday-name">{holiday.HolidayName}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Mobile message when no stylist selected */}
      {isMobile && !selectedStylist && (
        <div className="mobile-no-stylist-message">
          <div className="no-stylist-prompt">
            <User className="w-8 h-8 mb-2 text-gray-400" />
            <p className="text-lg font-medium text-gray-600">Select a stylist to view their schedule</p>
            <p className="text-sm text-gray-500 mt-1">Use the dropdown above to choose a stylist</p>
          </div>
        </div>
      )}
      
      {/* Day Grid - only show when we have stylists to display */}
      {(!isMobile || selectedStylist) && (
        <div className={`day-grid-container ${showSingleStylist ? 'single-stylist-mobile' : ''}`}>
          
          {/* STICKY HEADERS ROW - Direct child of scrolling container */}
          <div className="sticky-headers-row">
            <div className="time-header">Time</div>
            {stylistsToShow.map((stylist) => {
              const stylistAppointments = appointments.filter(apt => {
                // STRICT: Match by ID only - this is the primary identifier
                if (apt.MWuserID && stylist.id) {
                  return apt.MWuserID === stylist.id;
                }
                
                // FALLBACK: Only use name matching if no ID exists
                if (!apt.MWuserID && apt.MeetingWith) {
                  return apt.MeetingWith.toLowerCase() === stylist.name.toLowerCase();
                }
                
                return false;
              });
              
              return (
                <div key={stylist.id} className={`stylist-header ${showSingleStylist ? 'full-width' : ''}`}>
                  <User className="w-4 h-4" />
                  <span className="stylist-name">{stylist.name}</span>
                  <span className="appointment-count">
                    ({stylistAppointments.filter(apt => {
                      const aptStart = new Date(apt.EventStart);
                      return aptStart.toDateString() === currentDate.toDateString();
                    }).length})
                  </span>
                  {/* Mobile: Show date when viewing single stylist */}
                  {showSingleStylist && (
                    <span className="current-date">
                      {currentDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  )}
                  {/* Per-stylist lunch break settings toggle */}
                  <button
                    className={`lunch-settings-btn ${lunchBreakSettings[stylist.id]?.enabled ? 'lunch-active' : ''}`}
                    title="Lunch break settings"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (lunchSettingsFor?.stylistId === stylist.id) {
                        setLunchSettingsFor(null);
                        return;
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      setLunchSettingsFor({ stylistId: stylist.id, x: rect.left, y: rect.bottom + 6 });
                    }}
                  >
                    🍽
                  </button>
                </div>
              );
            })}
          </div>

          {/* CONTENT GRID - without headers */}
          <div className="day-grid-content">
            {/* Time column */}
            <div className="time-column">
              {timeSlots.map(timeSlot => (
                <div key={timeSlot} className="time-label">{timeSlot}</div>
              ))}
            </div>
            
            {/* Stylist columns */}
            <div className={`stylists-grid ${showSingleStylist ? 'single-stylist' : ''}`}>
            {stylistsToShow.map((stylist, stylistIndex) => {
                const stylistAppointments = appointments.filter(apt => {
                  // STRICT: Match by ID only - this is the primary identifier
                  if (apt.MWuserID && stylist.id) {
                    return apt.MWuserID === stylist.id;
                  }
                  
                  // FALLBACK: Only use name matching if no ID exists
                  if (!apt.MWuserID && apt.MeetingWith) {
                    return apt.MeetingWith.toLowerCase() === stylist.name.toLowerCase();
                  }
                  
                  return false;
                });
                
                return (
                  <div key={stylist.id} className={`stylist-column ${showSingleStylist ? 'full-width' : ''}`}>
                    <div className="stylist-schedule">
                      {/* Render empty time slots first */}
                      {timeSlots.map((timeSlot, timeSlotIndex) => (
                        <div
                          key={`${stylist.id}-${timeSlot}`}
                          className="time-slot drop-zone"
                          onClick={() => onTimeSlotClick(currentDate, timeSlot, stylist)}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, currentDate, timeSlot, stylist)}
                        >
                          <div className="add-appointment-btn">
                            <Plus className="w-3 h-3" />
                          </div>
                        </div>
                      ))}
                        {/* Hour divider lines - ADD HERE */}
  {generateHourLines().map(({ hour, topPosition, isMajorHour, timeLabel }) => (
    <div
      key={`hour-line-${hour}`}
      className={`hour-divider ${isMajorHour ? 'major-hour' : ''}`}
      style={{
        top: `${topPosition}px`
      }}
      title={timeLabel}
    />
  ))}
                      {/* Lunch break band */}
                      {renderLunchBreak(stylist)}

                      {/* Render ALL appointments for this stylist at schedule level */}
                      {(() => {
                        const dayAppointments = stylistAppointments.filter(apt => 
                          AppointmentServiceUtils.doesAppointmentSpanDate(apt, currentDate)
                        );
                        const overlapLayout = calculateOverlapLayout(dayAppointments, timeSlots);
                        
                        return dayAppointments.map((appointment) => {
                          const { effectiveStart, effectiveEnd } = AppointmentServiceUtils.getEffectiveAppointmentTimes(appointment, currentDate);
                          
                          // Bound the times to the calendar's visible hours
                          const calendarStart = new Date(currentDate);
                          calendarStart.setHours(TIME_SLOTS_CONFIG.START_HOUR, 0, 0, 0); // e.g., 8:00 AM
                          
                          const calendarEnd = new Date(currentDate);
                          calendarEnd.setHours(TIME_SLOTS_CONFIG.END_HOUR || 20, 0, 0, 0); // e.g., 8:00 PM
                          
                          // Use the later of effective start or calendar start
                          const boundedStart = effectiveStart < calendarStart ? calendarStart : effectiveStart;
                          // Use the earlier of effective end or calendar end
                          const boundedEnd = effectiveEnd > calendarEnd ? calendarEnd : effectiveEnd;
                          
                          const adjustedAppointment = {
                            ...appointment,
                            EventStart: boundedStart.toISOString(),
                            EventEnd: boundedEnd.toISOString()
                          };

                          const position = calculateAppointmentPosition(adjustedAppointment, timeSlots);
                          
                          return (
                            <div
                              key={appointment.ID}
                              data-appointment-id={appointment.ID}
                              draggable={!isResizing}
                              onDragStart={(e) => handleDragStart(e, appointment)}
                              onDragEnd={handleDragEnd}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAppointmentClick(appointment);
                              }}
                              className={`appointment-block spanning ${getAppointmentTypeClass(appointment.Title)} ${
                                position.durationMinutes > 180 ? 'long-appointment' : ''
                              } ${isResizing && resizeAppointmentId === appointment.ID ? 'resizing' : ''} ${
                                highlightedAppointment === appointment.ID ? 'highlighted' : ''
                              } ${showSingleStylist ? 'mobile-appointment' : ''}`}
                              style={{
                                height: `${position.heightInPixels}px`,
                                top: `${position.startSlotIndex * TIME_SLOTS_CONFIG.SLOT_HEIGHT_PX}px`,
                                zIndex: 10 + position.startSlotIndex,
                                width: overlapLayout[appointment.ID]?.width || '100%',
                                left: overlapLayout[appointment.ID]?.left || '0%',
                                position: 'absolute'
                              }}
                            >
                              {/* Top resize handle */}
                              <div 
                                className="resize-handle top"
                                onMouseDown={(e) => handleResizeStart(e, appointment, 'top')}
                              />
                              
                              <div className="appointment-content">
                                <div className="appointment-title">{appointment.ClientName}
                                  <div className="status-icons" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {renderStatusIcons(appointment)}
                                  </div>
                                </div>
                                <div className="appointment-subtitle">{appointment.Title}</div>
                                <div className="appointment-time">
                                  {formatTime(appointment.EventStart, useMilitaryTime)} - {formatTime(appointment.EventEnd, useMilitaryTime)}
                                </div>
                                {appointment.PhoneNum && (
                                  <div className="appointment-phone">{appointment.PhoneNum}</div>
                                )}
                              </div>
                              
                              {/* Bottom resize handle */}
                              <div 
                                className="resize-handle bottom"
                                onMouseDown={(e) => handleResizeStart(e, appointment, 'bottom')}
                              />
                            </div>
                          );
                        });
                      })()}

                      {/* Current time indicator */}
                      {(() => {
                        const timePosition = getCurrentTimePosition();
                        const isToday = currentDate.toDateString() === new Date().toDateString();
                        
                        return isToday && timePosition.isVisible && (
                          <div 
                            className="current-time-line"
                            style={{
                              position: 'absolute',
                              top: `${timePosition.top}px`,
                              left: '0',
                              right: '0',
                              height: '2px',
                              backgroundColor: '#ef4444',
                              zIndex: 1000,
                              pointerEvents: 'none',
                              boxShadow: '0 0 4px rgba(239, 68, 68, 0.5)'
                            }}
                          >
                            {/* Only show time label on first stylist column */}
                            {stylistIndex === 0 && (
                              <div 
                                className="current-time-label"
                                style={{
                                  position: 'absolute',
                                  left: '-60px',
                                  top: '-8px',
                                  fontSize: '12px',
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {currentTime.toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* Drag preview on grid */}
                      {dragPreview && dragAppointmentId && 
                      appointments.find(apt => apt.ID === dragAppointmentId)?.MWuserID === stylist.id && 
                      dragPreview.gridTop !== undefined && dragPreview.isValid && (
                        <div 
                          className="drag-preview-grid"
                          style={{
                            position: 'absolute',
                            top: `${dragPreview.gridTop}px`,
                            left: '0',
                            right: '0',
                            height: `${dragPreview.gridHeight}px`,
                            backgroundColor: 'rgba(59, 130, 246, 0.3)',
                            border: '2px dashed #3b82f6',
                            borderRadius: '4px',
                            zIndex: 999,
                            pointerEvents: 'none'
                          }}
                        >
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            left: '4px',
                            fontSize: '11px',
                            color: '#1e40af',
                            fontWeight: 'bold'
                          }}>
                            {dragPreview.startTime}
                          </div>
                        </div>
                      )}
                      
                      {/* Resize preview */}
                      {resizePreview && resizeAppointmentId && 
                      appointments.find(apt => apt.ID === resizeAppointmentId)?.MWuserID === stylist.id && (
                        <>
                          <div 
                            className="resize-preview"
                            style={{
                              top: `${resizePreview.top}px`,
                              height: `${resizePreview.height}px`
                            }}
                          />
                          <div 
                            className="resize-info"
                            style={{
                              left: `${resizePreview.mouseX + 10}px`,
                              top: `${resizePreview.mouseY - 60}px`
                            }}
                          >
                            <div>{resizePreview.startTime} - {resizePreview.endTime}</div>
                            <div>{Math.round(resizePreview.duration)} minutes</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Drag/Resize Tooltip */}
      {dragPreview && (
        <div className="drag-resize-tooltip">
          {dragPreview.startTime} - {dragPreview.endTime}
          <div style={{ fontSize: '10px', opacity: 0.8 }}>
            {dragPreview.duration}min
          </div>
        </div>
      )}

      {/* Per-stylist lunch break settings popover */}
      {lunchSettingsFor && (() => {
        const { stylistId, x, y } = lunchSettingsFor;
        const stylist = stylistsToShow.find(s => s.id === stylistId);
        if (!stylist) return null;
        const s = lunchBreakSettings[stylistId] || {
          enabled: false,
          startHour: 13,
          startMinute: 0,
          duration: 30
        };
        const change = (updates) => onLunchBreakSettingsChange && onLunchBreakSettingsChange(stylistId, updates);

        return (
          <div
            ref={lunchPopoverRef}
            className="lunch-settings-popover"
            style={{ position: 'fixed', left: Math.min(x, window.innerWidth - 230), top: y, zIndex: 9999 }}
          >
            <div className="lunch-popover-title">🍽 {stylist.name}'s Lunch</div>
            <label className="lunch-toggle">
              <input
                type="checkbox"
                checked={s.enabled}
                onChange={e => change({ enabled: e.target.checked })}
              />
              <span>Enable lunch break</span>
            </label>
            {s.enabled && (
              <>
                <div className="lunch-popover-row">
                  <label className="lunch-field-label">Time</label>
                  <input
                    type="time"
                    className="lunch-time-input"
                    value={`${s.startHour.toString().padStart(2, '0')}:${s.startMinute.toString().padStart(2, '0')}`}
                    onChange={e => {
                      const [h, m] = e.target.value.split(':').map(Number);
                      change({ startHour: h, startMinute: m });
                    }}
                  />
                </div>
                <div className="lunch-popover-row">
                  <label className="lunch-field-label">Duration</label>
                  <select
                    className="lunch-duration-select"
                    value={s.duration}
                    onChange={e => change({ duration: Number(e.target.value) })}
                  >
                    {[15, 30, 45, 60].map(d => (
                      <option key={d} value={d}>{d} min</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default DayView;